import React, { ReactElement, useCallback, useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Interpolate, TranslationContext } from '@ds/react-utils';
import { PhoneInput, PhoneNumber } from '@ds/components';
import { ContainerLayout } from 'layouts';
import { FontDetailS } from 'components/shared';
import { useButtonsLoading } from 'hooks';
import { routes } from 'static/routes';
import VerifyApplicationContext from 'contexts/VerifyApplicationContext';
import { submitPhoneNumber, sendCall, IPhoneSubmitResponse } from 'queries/PhoneQueries';
import { AxiosError } from 'axios';

const US_CALLING_CODE = '1';

const EnterPhoneNumberPage = (): ReactElement => {
    const context = useContext(VerifyApplicationContext);
    const history = useHistory();
    const translate = React.useContext(TranslationContext);

    const { isPrimaryButtonLoading, setIsPrimaryButtonLoading, areButtonsLoading } = useButtonsLoading();

    const [callingCode, setCallingCode] = useState<string>(US_CALLING_CODE);
    const [nationalNumber, setNationalNumber] = useState<string>('');
    const [extension, setExtension] = useState<string>('');
    const [error, setError] = useState<string>('');
    const [isPossible, setIsPossible] = useState<boolean>(true);
    const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

    const onChangeNumber = useCallback(({ callingCode: cc, nationalNumber: nn, isPossible: possible }: PhoneNumber) => {
        setCallingCode(cc ?? US_CALLING_CODE);
        setNationalNumber(nn);
        setIsPossible(possible);
        setError('');
    }, []);

    const onChangeExtension = useCallback((ext: string) => {
        setExtension(ext);
    }, []);

    const onSubmit = useCallback(() => {
        if (areButtonsLoading) {
            return;
        }

        setHasSubmitted(true);

        if (!nationalNumber.trim()) {
            setError(translate('Enter your phone number.'));
            return;
        }

        if (!isPossible) {
            return;
        }

        setError('');
        setIsPrimaryButtonLoading(true);

        submitPhoneNumber(
            {
                countryCode: '+' + callingCode,
                phoneNumber: nationalNumber.trim(),
                extension: extension.trim()
            },
            (response: IPhoneSubmitResponse) => {
                const submittedPhoneNumber = {
                    phoneNumber: response.maskedNumber,
                    uniqueId: response.uniqueId
                };

                sendCall(
                    {
                        phoneNumber: response.maskedNumber,
                        uniqueId: response.uniqueId,
                        language: context.language
                    },
                    () => {
                        setIsPrimaryButtonLoading(false);
                        history.push(routes.ConfirmCode, { selectedPhoneNumber: submittedPhoneNumber, method: 'PhoneCall' });
                    },
                    (sendError: AxiosError) => {
                        setIsPrimaryButtonLoading(false);
                        if (sendError?.response?.status === 401) {
                            history.push(routes.SessionExpired);
                        } else {
                            history.push(routes.Error);
                        }
                    }
                );
            },
            (submitError: AxiosError) => {
                setIsPrimaryButtonLoading(false);
                if (submitError?.response?.status === 401) {
                    history.push(routes.SessionExpired);
                } else if (submitError?.response?.status === 400) {
                    setError(translate('Enter your phone number.'));
                } else {
                    history.push(routes.Error);
                }
            }
        );
    }, [areButtonsLoading, nationalNumber, callingCode, extension, isPossible, context, history, translate, setIsPrimaryButtonLoading]);

    return (
        <ContainerLayout>
            <ContainerLayout.Header>
                <ContainerLayout.Header.Title
                    title={translate("Let's verify your phone number")}
                    titleDataQa="verify-enter-phone-title"
                />
            </ContainerLayout.Header>

            <ContainerLayout.InnerContent>
                <div className='narrow-desktop'>
                    <PhoneInput
                        data-qa="verify-enter-phone-number"
                        label={translate('Phone number')}
                        labelExt={translate('Extension')}
                        callingCode={callingCode}
                        nationalNumber={nationalNumber}
                        extension={extension}
                        onChangeNumber={onChangeNumber}
                        onChangeExtension={onChangeExtension}
                        showInvalidError={hasSubmitted && !isPossible}
                        error={error || undefined}
                        required
                        useCallingCodeMenu
                    />
                </div>
            </ContainerLayout.InnerContent>

            <ContainerLayout.Footer>
                <ContainerLayout.Footer.Disclaimer>
                    <FontDetailS id="enter-phone-legal-disclaimer">
                        <Interpolate
                            text={translate(
                                "By selecting <BoldText>[[Placeholder for 'Get a Call']]</BoldText> to receive a phone call, you agree to the use of artificial or prerecorded voice and automated systems. Message and data rates may apply."
                            )}
                            components={{
                                BoldText: () => <strong>{translate('Get a Call')}</strong>
                            }}
                        />
                    </FontDetailS>
                </ContainerLayout.Footer.Disclaimer>
                <ContainerLayout.Footer.Buttons>
                    <ContainerLayout.Footer.Buttons.Primary
                        data-qa="verify-enter-phone-submit-button"
                        text={translate('Get a Call')}
                        onClick={onSubmit}
                        loading={isPrimaryButtonLoading}
                        aria-describedby="enter-phone-legal-disclaimer"
                    />
                </ContainerLayout.Footer.Buttons>
            </ContainerLayout.Footer>
        </ContainerLayout>
    );
};

export default EnterPhoneNumberPage;
